'use client'

import type { CSSProperties } from 'react'
import { GolfBall } from './GolfBall'
import type { TeeOffBeat } from './teeOff'

type Props = Readonly<{
  beat: TeeOffBeat
  /** The one word over the count — digits need no translation. */
  eyebrow: string
  className?: string
  style?: CSSProperties
}>

/**
 * The dial around the numeral. Deliberately the avatar countdown's geometry
 * shrunk to the pill (`BirdieCard.AvatarCountdown`, same 3-px-ish stroke on a
 * track, same twelve-o'clock start): one game, one clock face, not a second
 * widget dropped on the video.
 */
const DIAL_BOX = 22
const DIAL_RADIUS = 9
const DIAL_CIRCUMFERENCE = Math.round(2 * Math.PI * DIAL_RADIUS * 100) / 100

/**
 * The tee-off countdown pill (B37) — the SAME slot, chrome and entry the putt
 * callout uses, so the count is replaced by "Putt 1 of 3" rather than joined by
 * it. `teeOff.ts` decides the beat; this only draws it.
 *
 * The dial is keyed on the beat so each numeral remounts and its ring closes
 * from the top again. `aria-hidden` on the dial and one polite announcement of
 * the eyebrow: a screen reader that read "three, two, one" a second apart would
 * be talking over the round it is introducing.
 */
export function BirdieTeeOff({ beat, eyebrow, className, style }: Props) {
  const classes = ['birdie-callout', 'birdie-callout--teeoff', className ?? '']
    .filter(Boolean)
    .join(' ')
  return (
    <div aria-live="polite" className={classes} role="status" style={style}>
      <span className="birdie-callout__eyebrow">{eyebrow}</span>
      <span aria-hidden="true" className="birdie-callout__dial" key={String(beat)}>
        <svg
          className="birdie-callout__dial-ring"
          height={DIAL_BOX}
          style={{ '--birdie-tee-arc': DIAL_CIRCUMFERENCE } as CSSProperties}
          viewBox={`0 0 ${DIAL_BOX} ${DIAL_BOX}`}
          width={DIAL_BOX}
        >
          <circle
            className="birdie-callout__dial-track"
            cx={DIAL_BOX / 2}
            cy={DIAL_BOX / 2}
            fill="none"
            r={DIAL_RADIUS}
          />
          <circle
            className="birdie-callout__dial-arc"
            cx={DIAL_BOX / 2}
            cy={DIAL_BOX / 2}
            fill="none"
            r={DIAL_RADIUS}
          />
        </svg>
        {beat === 'ready' ? (
          <span className="birdie-callout__dial-ball">
            <GolfBall className="birdie-callout__dial-ball-svg" />
          </span>
        ) : (
          <span className="birdie-callout__digit">{beat}</span>
        )}
      </span>
    </div>
  )
}
